<template lang="pug">
.mis-tareas
  //- ── Hero "Mi día" ───────────────────────────────────────────────
  .mt-hero
    .mt-hero__txt
      .mt-hero__date {{ todayLabel }}
      h1.mt-hero__hi Hola, {{ firstName }}
      .mt-hero__sub {{ heroSub }}
      button.mt-hero__btn(@click="openPrograms")
        span(v-html="rawIcon('layers')")
        | Mis programas
    .mt-ring
      svg(width="112" height="112" viewBox="0 0 112 112")
        circle(cx="56" cy="56" r="47" fill="none" stroke="rgba(255,255,255,.22)" stroke-width="10")
        circle.mt-ring__fill(
          cx="56" cy="56" r="47" fill="none" stroke="#fff" stroke-width="10"
          stroke-linecap="round" :stroke-dasharray="ringCirc" :stroke-dashoffset="ringOffset"
        )
      .mt-ring__lbl
        b {{ doneCount }}/{{ totalToday }}
        span hoy

  //- ── Loading ─────────────────────────────────────────────────────
  .mt-loading(v-if="loading")
    .mt-spinner
    span Cargando tus tareas…

  //- ── Vista: Mis tareas ───────────────────────────────────────────
  .mt-view(v-show="!loading")
    .mt-filters
      button.mt-chip(
        v-for="f in typeFilters"
        :key="f.key"
        :class="{ 'is-active': filter === f.key }"
        @click="filter = f.key"
      )
        span.mt-chip__ico(v-if="f.icon" v-html="rawIcon(f.icon)")
        | {{ f.label }}

    template(v-for="g in taskGroups")
      section.mt-group(v-if="g.items.length || filter === 'all'" :key="g.key" :class="g.cls")
        .mt-group__head
          span.mt-group__dot
          h2 {{ g.title }}
          span.mt-group__count {{ g.items.length }}
          button.mt-group__toggle(v-if="g.key === 'done'" @click="doneCollapsed = !doneCollapsed")
            span(v-html="rawIcon('chevron')" :style="doneCollapsed ? 'transform:rotate(-90deg);display:inline-flex' : 'display:inline-flex'")
        .mt-cards(v-show="!(g.key === 'done' && doneCollapsed)")
          .mt-empty(v-if="!g.items.length") Nada por acá.
          .mt-card(
            v-for="t in g.items"
            :key="t._id"
            :class="['type-' + t.type, { 'is-leaving': leavingId === t._id, 'is-done': g.key === 'done' }]"
          )
            .mt-card__ico(v-html="typeIcon(t.type)")
            .mt-card__body
              .mt-card__name {{ taskName(t) }}
              .mt-card__meta
                span.mt-card__kind {{ typeLabel(t.type) }}
                span ·
                span.mt-card__when {{ whenLabel(t) }}
                span.mt-badge(:class="g.badge") {{ g.key === 'done' ? 'Completada' : g.badgeTxt }}
            .mt-card__action
              span.mt-btn.mt-btn--done(v-if="g.key === 'done'" v-html="'&#10003; Listo'")
              button.mt-btn.mt-btn--teal(
                v-else-if="t.type === 'medications'"
                :disabled="completingId === t._id"
                @click="completeTask(t)"
              )
                span(v-html="rawIcon('check')")
                | {{ actionLabel(t) }}
              button.mt-btn.mt-btn--ghost(
                v-else-if="!isCompletable(t)"
                @click="openDrawer(t)"
              ) Ver
              button.mt-btn.mt-btn--ghost(
                v-else-if="t.type === 'exercises' || t.type === 'recommendations'"
                @click="openDrawer(t)"
              ) {{ actionLabel(t) }}
              button.mt-btn.mt-btn--primary(v-else @click="openDrawer(t)") {{ actionLabel(t) }}

  //- ── Drawer de detalle (custom — Element el-drawer no está registrado) ──
  .mt-scrim(v-show="drawer.visible" @click="closeDrawer")
  aside.mt-drawer(:class="{ 'is-open': drawer.visible }")
    .mt-d(v-if="drawer.task")
      .mt-d__head(:class="'type-' + drawer.task.type")
        .mt-card__ico(v-html="typeIcon(drawer.task.type)")
        div
          h3 {{ taskName(drawer.task) }}
          .mt-d__sub {{ drawerSub }}
        button.mt-d__close(@click="drawer.visible = false") ✕

      //- Vital
      .mt-d__body(v-if="drawer.mode === 'vital'")
        .mt-field(v-for="av in drawer.task.answerValues" :key="av.name")
          label {{ av.name }}
          .mt-field__row
            input(
              type="text" inputmode="decimal" placeholder="—"
              v-model="vitalInputs[av.name]"
            )
          .mt-range(v-if="rangeText(av)")
            | Rango normal
            span.mt-range__pill {{ rangeText(av) }}
      .mt-d__foot(v-if="drawer.mode === 'vital'")
        button.mt-btn.mt-btn--ghost(@click="drawer.visible = false") Cancelar
        button.mt-btn.mt-btn--teal(:disabled="savingDrawer || !vitalReady" @click="submitVital")
          span(v-html="rawIcon('check')")
          | Guardar

      //- Checkin (wizard)
      .mt-d__body(v-if="drawer.mode === 'checkin'")
        .mt-wiz-prog
          .mt-wiz-bar
            i(:style="{ width: wizPct + '%' }")
          span {{ wizStep + 1 }}/{{ wizTotal }}
        p.mt-wiz-q {{ wizQuestion }}
        .mt-opt(
          v-for="ans in wizAnswers"
          :key="ans._id"
          :class="{ 'is-sel': isPicked(ans._id) }"
          @click="pickWiz(ans._id)"
        )
          span.mt-opt__rc
          | {{ ans.content }}
      .mt-d__foot(v-if="drawer.mode === 'checkin'")
        button.mt-btn.mt-btn--ghost(v-if="wizStep > 0" @click="wizStep--") Atrás
        button.mt-btn.mt-btn--teal(:disabled="!wizStepAnswered || savingDrawer" @click="wizNext")
          span(v-if="wizStep === wizTotal - 1" v-html="rawIcon('check')")
          | {{ wizStep === wizTotal - 1 ? 'Finalizar' : 'Siguiente' }}
          span(v-if="wizStep < wizTotal - 1" v-html="rawIcon('chevron')")

      //- Exercise
      .mt-d__body(v-if="drawer.mode === 'exercise'")
        .mt-media.cover-pat(v-html="rawIcon('play')")
        .mt-exp
          .mt-exp__m(v-if="drawer.task.duration")
            b {{ drawer.task.duration }}
            span Duración
          .mt-exp__m(v-if="drawer.task.series")
            b {{ drawer.task.series }}
            span Series
          .mt-exp__m(v-if="drawer.task.repetitions")
            b {{ drawer.task.repetitions }}
            span Reps
        .mt-rtext(v-if="drawer.task.description") {{ drawer.task.description }}
      .mt-d__foot(v-if="drawer.mode === 'exercise'")
        button.mt-btn.mt-btn--ghost(@click="drawer.visible = false") Cerrar
        button.mt-btn.mt-btn--teal(:disabled="savingDrawer" @click="completeFromDrawer('¡Bien hecho!')")
          span(v-html="rawIcon('check')")
          | Marcar hecho

      //- Recommendation (tarea del día) — contenido real (media/YouTube/texto)
      .mt-d__body(v-if="drawer.mode === 'recommendation'")
        .mt-rec-content(v-html="drawer.contentHtml")
      .mt-d__foot(v-if="drawer.mode === 'recommendation'")
        button.mt-btn.mt-btn--teal.is-full(:disabled="savingDrawer" @click="completeFromDrawer('Marcada como leída')")
          span(v-html="rawIcon('check')")
          | Listo, lo leí

      //- Appointment (solo lectura)
      .mt-d__body(v-if="drawer.mode === 'appointment'")
        .mt-rtext
          p {{ taskName(drawer.task) }}
          p(v-if="drawer.task.description") {{ drawer.task.description }}
          p.mt-muted Llevá tus últimos estudios y la lista de medicación actual.
      .mt-d__foot(v-if="drawer.mode === 'appointment'")
        button.mt-btn.mt-btn--ghost.is-full(@click="drawer.visible = false") Cerrar

  //- ── Drawer "Mis programas" ───────────────────────────────────────
  .mt-scrim(v-show="programs.visible" @click="closePrograms")
  aside.mt-drawer(:class="{ 'is-open': programs.visible }")
    .mt-d
      .mt-d__head.type-recommendations
        .mt-card__ico(v-html="rawIcon('layers')")
        div
          h3 Mis programas
          .mt-d__sub Los programas de salud en los que estás participando
        button.mt-d__close(@click="closePrograms") ✕
      .mt-d__body
        .mt-empty(v-if="programs.loading") Cargando…
        .mt-empty(v-else-if="!programs.items.length") Todavía no estás en ningún programa.
        .mt-prog(v-for="p in programs.items" :key="p._id")
          .mt-prog__ico(v-html="rawIcon('layers')")
          .mt-prog__body
            .mt-prog__name {{ programName(p) }}
            .mt-prog__desc(v-if="programDesc(p)") {{ programDesc(p) }}
            .mt-prog__meta
              span.mt-prog__status(:class="programStatus(p).cls") {{ programStatus(p).label }}
              span.mt-prog__since(v-if="programSince(p)") {{ programSince(p) }}
      .mt-d__foot
        button.mt-btn.mt-btn--ghost.is-full(@click="closePrograms") Cerrar
</template>

<script>
import { mapGetters } from "vuex";
import patientTaskService, {
  COMPLETABLE_TASK_TYPES,
} from "@/services/patient-task";
import patientProgramsService from "@/services/patient-programs";

/**
 * @component MisTareas
 * @description Portal del paciente — vista "Mis tareas". Muestra la agenda
 *   unificada de tareas (registros/signos vitales, medicación, check-ins,
 *   ejercicios, recomendaciones, recordatorios de turno) agrupada por urgencia,
 *   con acciones de completar y carga de datos, más una sección
 *   "Recomendaciones" separada en Información relevante y Campañas.
 *   Reusa los endpoints del API que ya consume la app mobile (backend intacto).
 * @example <MisTareas />
 */

const TYPE_META = {
  vitals: { label: "Registros", icon: "vital", action: "Cargar" },
  medications: { label: "Medicación", icon: "medication", action: "Tomé" },
  checkins: { label: "Check-in", icon: "checkin", action: "Responder" },
  exercises: { label: "Ejercicios", icon: "exercise", action: "Hecho" },
  recommendations: {
    label: "Recomendaciones",
    icon: "recommendation",
    action: "Leer",
  },
  appointments: { label: "Recordatorios", icon: "appointment", action: "Ver" },
};

const ICONS = {
  vital:
    '<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8z"/>',
  medication:
    '<rect x="3" y="8" width="18" height="8" rx="4"/><path d="M12 8v8"/>',
  checkin:
    '<path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>',
  exercise:
    '<path d="M6.5 6.5 17.5 17.5"/><path d="M4 8v8M20 8v8M2 10v4M22 10v4"/>',
  recommendation:
    '<circle cx="12" cy="12" r="9"/><path d="M12 8h.01M11 12h1v4h1"/>',
  appointment:
    '<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>',
  chevron: '<path d="M6 9l6 6 6-6"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  play: '<path d="M8 5v14l11-7z"/>',
  layers:
    '<path d="M12 2 2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>',
  campaign:
    '<path d="M3 11l18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 0 1-5.8-1.6"/>',
  hospital:
    '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M12 8v8M8 12h8"/>',
  bulb: '<path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12c.7.7 1 1.5 1 2h6c0-.5.3-1.3 1-2A7 7 0 0 0 12 2z"/>',
  building:
    '<rect x="4" y="3" width="16" height="18" rx="1"/><path d="M9 21v-4h6v4M8 7h.01M12 7h.01M16 7h.01M8 11h.01M12 11h.01M16 11h.01"/>',
};

const GROUPS = [
  {
    key: "overdue",
    title: "Vencidas",
    cls: "g-overdue",
    badge: "b-overdue",
    badgeTxt: "Vencida",
  },
  {
    key: "today",
    title: "Para hoy",
    cls: "g-today",
    badge: "b-today",
    badgeTxt: "Hoy",
  },
  {
    key: "upcoming",
    title: "Próximas",
    cls: "g-upcoming",
    badge: "b-upcoming",
    badgeTxt: "Próxima",
  },
  {
    key: "done",
    title: "Completadas hoy",
    cls: "g-done",
    badge: "b-done",
    badgeTxt: "Completada",
  },
];

export default {
  name: "MisTareas",
  data() {
    return {
      loading: true,
      view: "tasks",
      filter: "all",
      tasks: [],
      recommendations: [],
      doneCollapsed: false,
      completingId: null,
      leavingId: null,
      savingDrawer: false,
      drawer: { visible: false, task: null, mode: null, contentHtml: "" },
      programs: { visible: false, loading: false, items: [] },
      vitalInputs: {},
      wizStep: 0,
      wizSelected: {},
      ringCirc: 2 * Math.PI * 47,
    };
  },
  computed: {
    ...mapGetters(["user", "isPatient"]),
    patientId() {
      return this.user && this.user._id ? this.user._id : null;
    },
    firstName() {
      const n = this.user && this.user.firstName ? this.user.firstName : "";
      return n ? n.charAt(0).toUpperCase() + n.slice(1) : "";
    },
    todayLabel() {
      const d = new Date();
      const s = d.toLocaleDateString("es-AR", {
        weekday: "long",
        day: "numeric",
        month: "long",
      });
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    typeFilters() {
      return [
        { key: "all", label: "Todas", icon: null },
        ...Object.keys(TYPE_META).map((k) => ({
          key: k,
          label: TYPE_META[k].label,
          icon: TYPE_META[k].icon,
        })),
      ];
    },
    filteredTasks() {
      if (this.filter === "all") return this.tasks;
      return this.tasks.filter((t) => t.type === this.filter);
    },
    taskGroups() {
      return GROUPS.map((g) => ({
        ...g,
        items: this.filteredTasks.filter((t) => this.groupOf(t) === g.key),
      }));
    },
    pendingCount() {
      return this.tasks.filter(
        (t) => !t.completedAt && this.groupOf(t) !== "upcoming"
      ).length;
    },
    doneCount() {
      return this.tasks.filter((t) => this.groupOf(t) === "done").length;
    },
    totalToday() {
      const relevant = this.tasks.filter((t) => {
        const g = this.groupOf(t);
        return g === "overdue" || g === "today" || g === "done";
      });
      return relevant.length || this.doneCount;
    },
    ringOffset() {
      const pct = this.totalToday
        ? Math.min(1, this.doneCount / this.totalToday)
        : 0;
      return this.ringCirc * (1 - pct);
    },
    heroSub() {
      const left = Math.max(0, this.totalToday - this.doneCount);
      if (!this.tasks.length) return "No tenés tareas pendientes por ahora.";
      if (!left) return "¡Completaste todo por hoy! 🎉";
      return `Te ${left === 1 ? "queda" : "quedan"} ${left} tarea${
        left === 1 ? "" : "s"
      } para hoy.`;
    },
    recInfo() {
      return this.recommendations.filter(
        (r) => String(r.category) !== "category2"
      );
    },
    recCamp() {
      return this.recommendations.filter(
        (r) => String(r.category) === "category2"
      );
    },
    drawerSub() {
      const t = this.drawer.task;
      if (!t) return "";
      if (this.drawer.mode === "appointment") return "Recordatorio · Cita";
      if (
        this.drawer.mode === "rec-browse" ||
        this.drawer.mode === "recommendation"
      )
        return "Recomendación · " + this.categoryLabel(t.category);
      return this.typeLabel(t.type) + " · " + this.whenLabel(t);
    },
    vitalReady() {
      const t = this.drawer.task;
      if (!t || !t.answerValues) return false;
      return t.answerValues.every(
        (av) =>
          this.vitalInputs[av.name] !== undefined &&
          this.vitalInputs[av.name] !== ""
      );
    },
    wizAnswerValue() {
      const t = this.drawer.task;
      if (!t || !t.answerValues || !t.answerValues[this.wizStep]) return null;
      return t.answerValues[this.wizStep];
    },
    wizTotal() {
      const t = this.drawer.task;
      return t && t.answerValues ? t.answerValues.length : 0;
    },
    wizQuestion() {
      return this.wizAnswerValue ? this.wizAnswerValue.question : "";
    },
    wizAnswers() {
      return this.wizAnswerValue && this.wizAnswerValue.answers
        ? this.wizAnswerValue.answers
        : [];
    },
    wizPct() {
      return this.wizTotal
        ? Math.round((this.wizStep / this.wizTotal) * 100)
        : 0;
    },
    wizStepAnswered() {
      const sel = this.wizSelected[this.wizStep];
      return !!(sel && sel.length);
    },
  },
  created() {
    this.fetch();
  },
  methods: {
    rawIcon(name) {
      const inner = ICONS[name] || "";
      return (
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        inner +
        "</svg>"
      );
    },
    typeIcon(type) {
      const meta = TYPE_META[type];
      return this.rawIcon(meta ? meta.icon : "recommendation");
    },
    /**
     * @description Resuelve la URL de la media de una tarea (recomendación /
     *              ejercicio). Tolera varios shapes de `media.content`.
     * @param {Object} task
     * @returns {string}
     */
    mediaRaw(task) {
      const m = task && task.media;
      if (!m) return "";
      if (typeof m.content === "string") return m.content;
      if (Array.isArray(m.content) && m.content[0]) {
        const c = m.content[0];
        return (
          (c && (c.value || c.url)) || (typeof c === "string" ? c : "") || ""
        );
      }
      return m.url || m.value || "";
    },
    /**
     * @description Extrae el ID de un video de YouTube de una URL (watch/embed/
     *              youtu.be/shorts). Devuelve null si no es YouTube.
     * @param {string} url
     * @returns {string|null}
     */
    youtubeId(url) {
      if (!url) return null;
      const m = String(url).match(
        /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([\w-]{11})/
      );
      return m ? m[1] : null;
    },
    /**
     * @description Arma el HTML del contenido de una recomendación: media
     *              (imagen / video / YouTube embebido / texto) + descripción +
     *              links. Escapa el texto; el iframe se construye con un ID de
     *              YouTube validado.
     * @param {Object} task
     * @param {string|null} [resolvedUrl] - URL ya resuelta (signed o YouTube).
     * @returns {string} HTML
     */
    recContentHtml(task, resolvedUrl) {
      const esc = (s) =>
        String(s == null ? "" : s).replace(
          /[&<>"]/g,
          (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c])
        );
      const m = (task && task.media) || {};
      const raw = this.mediaRaw(task);
      const url = resolvedUrl || (/^https?:\/\//i.test(raw) ? raw : "");
      const type = m.type || "";
      const yid = this.youtubeId(url);
      const parts = [];
      if (yid) {
        parts.push(
          '<div class="mt-embed"><iframe src="https://www.youtube.com/embed/' +
            yid +
            '" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>'
        );
      } else if (type === "video" && url) {
        parts.push(
          '<video class="mt-media-el" controls src="' +
            esc(url) +
            '" onerror="this.style.display=\'none\'"></video>'
        );
      } else if (type === "image" && url) {
        parts.push(
          '<img class="mt-media-el" src="' +
            esc(url) +
            '" alt="" onerror="this.style.display=\'none\'" />'
        );
      } else if (type === "text" && url) {
        parts.push('<p class="mt-rtext">' + esc(url) + "</p>");
      }
      if (task && task.description) {
        parts.push('<p class="mt-rtext">' + esc(task.description) + "</p>");
      }
      if (task && Array.isArray(task.links)) {
        task.links.forEach((l) => {
          if (l && l.url) {
            parts.push(
              '<a class="mt-rec-link" href="' +
                esc(l.url) +
                '" target="_blank" rel="noopener">' +
                esc(l.name || l.url) +
                "</a>"
            );
          }
        });
      }
      if (!parts.length) {
        parts.push(
          '<div class="mt-empty-content">Esta recomendación todavía no tiene contenido para mostrar.</div>'
        );
      }
      return parts.join("");
    },
    typeLabel(type) {
      return TYPE_META[type] ? TYPE_META[type].label : type;
    },
    actionLabel(t) {
      return TYPE_META[t.type] ? TYPE_META[t.type].action : "Ver";
    },
    isCompletable(t) {
      return COMPLETABLE_TASK_TYPES.indexOf(t.type) !== -1;
    },
    taskName(t) {
      return t && t.name ? t.name : "Tarea";
    },
    groupOf(t) {
      if (t.completedAt) return "done";
      if (!t.scheduledAt) return "today";
      const d = new Date(t.scheduledAt);
      const now = new Date();
      const startToday = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      );
      const endToday = new Date(startToday.getTime() + 86400000);
      if (d < startToday) return "overdue";
      if (d < endToday) return "today";
      return "upcoming";
    },
    whenLabel(t) {
      const raw = t.completedAt || t.scheduledAt;
      if (!raw) return "";
      const d = new Date(raw);
      const now = new Date();
      const startToday = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      );
      const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const diffDays = Math.round((dayStart - startToday) / 86400000);
      const hm = d.toLocaleTimeString("es-AR", {
        hour: "2-digit",
        minute: "2-digit",
      });
      let day = "";
      if (diffDays === 0) day = "hoy";
      else if (diffDays === -1) day = "ayer";
      else if (diffDays === 1) day = "mañana";
      else
        day = d.toLocaleDateString("es-AR", {
          weekday: "short",
          day: "numeric",
        });
      return `${day} ${hm}`;
    },
    rangeText(av) {
      const g = av && av.measurement && av.measurement.green;
      if (!g || g.min === undefined || g.max === undefined) return "";
      return `${g.min}–${g.max}`;
    },
    categoryLabel(cat) {
      const map = {
        category1: "Patología",
        category2: "Campaña",
        category3: "Institucional",
        category4: "Tips de salud",
      };
      return map[cat] || "General";
    },
    categoryCover(cat) {
      const map = {
        category1: "cover-pat",
        category2: "cover-camp",
        category3: "cover-inst",
        category4: "cover-tip",
      };
      return map[cat] || "cover-inst";
    },
    categoryIcon(cat) {
      const map = {
        category1: "hospital",
        category2: "campaign",
        category3: "building",
        category4: "bulb",
      };
      return map[cat] || "recommendation";
    },
    async fetch() {
      this.loading = true;
      try {
        const [tasks, recs] = await Promise.all([
          patientTaskService.getMyTasks(),
          patientTaskService.getMyRecommendations(),
        ]);
        this.tasks = tasks;
        this.recommendations = recs;
        this.$store.commit("setPatientPendingTasks", this.pendingCount);
      } catch (err) {
        this.$message && this.$message.error("No pudimos cargar tus tareas.");
      } finally {
        this.loading = false;
      }
    },
    onAction(t) {
      if (t.type === "medications") this.completeTask(t);
      else this.openDrawer(t);
    },
    async openDrawer(t) {
      let mode = t.type === "vitals" ? "vital" : null;
      if (t.type === "checkins") mode = "checkin";
      else if (t.type === "exercises") mode = "exercise";
      else if (t.type === "recommendations") mode = "recommendation";
      else if (t.type === "appointments") mode = "appointment";
      // Vue 2: inicializar las claves de vitalInputs para que el v-model sea
      // reactivo. Asignar a una clave nueva de un objeto vacío NO dispara
      // reactividad, así que `vitalReady` no se recalcularía y "Guardar"
      // quedaría deshabilitado para siempre.
      const vitalInputs = {};
      if (t.type === "vitals" && Array.isArray(t.answerValues)) {
        t.answerValues.forEach((av) => {
          vitalInputs[av.name] = "";
        });
      }
      this.vitalInputs = vitalInputs;
      this.wizStep = 0;
      this.wizSelected = {};
      const raw = this.mediaRaw(t);
      const httpUrl = /^https?:\/\//i.test(raw) ? raw : null;
      this.drawer = {
        visible: true,
        task: t,
        mode,
        contentHtml:
          mode === "recommendation" ? this.recContentHtml(t, httpUrl) : "",
      };
      // Recomendación: resolvemos la media (signed URL) y, para imagen/video,
      // precargamos para no dejar el drawer vacío si el archivo no existe.
      if (mode === "recommendation") {
        let url = httpUrl;
        if (raw && !httpUrl) {
          url = await patientTaskService.getRecommendationMediaUrl(t._id);
        }
        if (!this.drawer.task || this.drawer.task._id !== t._id) return;
        let finalUrl = url;
        const type = (t.media && t.media.type) || "";
        if (
          url &&
          (type === "image" || type === "video") &&
          !this.youtubeId(url)
        ) {
          const ok = await this.mediaLoads(url, type);
          if (!this.drawer.task || this.drawer.task._id !== t._id) return;
          finalUrl = ok ? url : null;
        }
        this.$set(this.drawer, "contentHtml", this.recContentHtml(t, finalUrl));
      }
    },
    /**
     * @description Precarga una imagen/video para confirmar que el archivo
     *              existe (la signed URL puede apuntar a un objeto ausente).
     * @param {string} url
     * @param {string} type - 'image' | 'video'
     * @returns {Promise<boolean>}
     */
    mediaLoads(url, type) {
      return new Promise((resolve) => {
        let done = false;
        const finish = (ok) => {
          if (!done) {
            done = true;
            resolve(ok);
          }
        };
        if (type === "image") {
          const img = new Image();
          img.onload = () => finish(true);
          img.onerror = () => finish(false);
          img.src = url;
        } else {
          finish(true);
        }
        setTimeout(() => finish(false), 6000);
      });
    },
    openRec(r) {
      this.drawer = { visible: true, task: r, mode: "rec-browse" };
    },
    closeDrawer() {
      this.drawer.visible = false;
    },
    /**
     * @description Abre el drawer "Mis programas" y trae los programas del
     *              paciente (una sola vez).
     * @returns {Promise<void>}
     */
    async openPrograms() {
      this.programs.visible = true;
      if (this.programs.items.length || !this.patientId) return;
      this.programs.loading = true;
      try {
        this.programs.items = await patientProgramsService.getMyPrograms(
          this.patientId
        );
      } catch (err) {
        this.programs.items = [];
      } finally {
        this.programs.loading = false;
      }
    },
    closePrograms() {
      this.programs.visible = false;
    },
    programName(p) {
      return p && p.name ? p.name : "Programa";
    },
    programDesc(p) {
      return p && p.description ? p.description : "";
    },
    /**
     * @description Etiqueta + clase visual del estado del ProgramPatient.
     * @param {Object} p
     * @returns {{label:string, cls:string}}
     */
    programStatus(p) {
      const s = (p && p.status) || "";
      const map = {
        active: { label: "Activo", cls: "is-active" },
        in_progress: { label: "En curso", cls: "is-active" },
        completed: { label: "Finalizado", cls: "is-done" },
        pending: { label: "Pendiente", cls: "is-pending" },
      };
      return map[s] || { label: s || "Activo", cls: "is-active" };
    },
    programSince(p) {
      const d = p && p.since ? new Date(p.since) : null;
      if (!d || Number.isNaN(d.getTime())) return "";
      return (
        "Desde " +
        d.toLocaleDateString("es-AR", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      );
    },
    async completeTask(t, payload, msg) {
      if (this.completingId === t._id || t.completedAt) return;
      if (!this.patientId) return;
      this.completingId = t._id;
      try {
        await patientTaskService.complete({
          taskType: t.type,
          taskID: t._id,
          patientID: this.patientId,
          payload,
        });
        this.leavingId = t._id;
        setTimeout(() => {
          // Vue 2 no detecta propiedades nuevas: la tarea viene del API sin
          // `completedAt`, así que hay que setearla reactivamente con $set para
          // que se recomputen los grupos y baje a "Completadas" sin refrescar.
          this.$set(t, "completedAt", new Date().toISOString());
          this.leavingId = null;
          this.$store.commit("setPatientPendingTasks", this.pendingCount);
        }, 260);
        this.$message && this.$message.success(msg || "Tarea completada");
      } catch (err) {
        const status = err && err.response ? err.response.status : null;
        this.$message &&
          this.$message.error(
            status === 400
              ? "Esta tarea ya fue registrada o todavía no es su horario."
              : "No pudimos completar la tarea."
          );
      } finally {
        this.completingId = null;
      }
    },
    async completeFromDrawer(msg) {
      const t = this.drawer.task;
      this.drawer.visible = false;
      await this.completeTask(t, undefined, msg);
    },
    async submitVital() {
      const t = this.drawer.task;
      const payload = {
        answer: t.answerValues.map((av) => ({
          name: av.name,
          value: this.vitalInputs[av.name],
        })),
      };
      this.savingDrawer = true;
      this.drawer.visible = false;
      await this.completeTask(t, payload, "Medición guardada");
      this.savingDrawer = false;
    },
    isPicked(answerId) {
      const sel = this.wizSelected[this.wizStep] || [];
      return sel.indexOf(answerId) !== -1;
    },
    pickWiz(answerId) {
      // Selección única por pregunta (reemplaza). El backend acepta multi;
      // v1 mantiene una respuesta por pregunta para simplicidad.
      this.$set(this.wizSelected, this.wizStep, [answerId]);
    },
    wizNext() {
      if (!this.wizStepAnswered) return;
      if (this.wizStep < this.wizTotal - 1) {
        this.wizStep += 1;
      } else {
        this.submitCheckin();
      }
    },
    async submitCheckin() {
      const t = this.drawer.task;
      const payload = {
        answerValues: t.answerValues.map((av, i) => ({
          selected: this.wizSelected[i] || [],
        })),
      };
      this.savingDrawer = true;
      this.drawer.visible = false;
      await this.completeTask(t, payload, "Check-in enviado");
      this.savingDrawer = false;
    },
  },
};
</script>

<style lang="scss" scoped>
$navy: #1b2b63;
$navy-700: #243a86;
$teal: #12a594;
$teal-700: #0e8578;
$ink: #1b2233;
$muted: #697392;
$faint: #98a2bd;
$surface: #ffffff;
$surface-2: #f6f8fc;
$border: #e6eaf3;
$border-strong: #d5dcec;

.mis-tareas {
  max-width: 940px;
  margin: 0 auto;
  padding: 20px 18px 60px;
  font-family: "Plus Jakarta Sans", system-ui, -apple-system, Segoe UI, Roboto,
    sans-serif;
  color: $ink;
}

/* hero */
.mt-hero {
  display: flex;
  align-items: center;
  gap: 22px;
  flex-wrap: wrap;
  background: $navy;
  color: #fff;
  border-radius: 20px;
  padding: 22px 24px;
  box-shadow: 0 20px 44px rgba(20, 28, 54, 0.16);
}
.mt-hero__txt {
  flex: 1;
  min-width: 200px;
}
.mt-hero__date {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  opacity: 0.82;
}
.mt-hero__hi {
  color: #fff; // un h1 global del dashboard pisa el color heredado si no se fija
  font-size: 20px;
  font-weight: 700;
  margin: 3px 0 5px;
  letter-spacing: -0.02em;
}
.mt-hero__sub {
  font-size: 13px;
  opacity: 0.9;
}
.mt-hero__btn {
  margin-top: 14px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 700;
  color: #fff;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 9px;
  padding: 8px 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  transition: background 0.15s;
}
.mt-hero__btn:hover {
  background: rgba(255, 255, 255, 0.24);
}
.mt-hero__btn ::v-deep svg {
  width: 15px;
  height: 15px;
}
/* items del drawer "Mis programas" */
.mt-prog {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid $border;
}
.mt-prog:last-child {
  border-bottom: none;
}
.mt-prog__ico {
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  background: #e7ecfb;
  color: #3a63c4;
}
.mt-prog__ico ::v-deep svg {
  width: 18px;
  height: 18px;
}
.mt-prog__body {
  flex: 1;
  min-width: 0;
}
.mt-prog__name {
  font-weight: 700;
  font-size: 14px;
  color: #0f172a;
}
.mt-prog__desc {
  color: $muted;
  font-size: 12.5px;
  margin-top: 3px;
  line-height: 1.5;
}
.mt-prog__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 7px;
  flex-wrap: wrap;
}
.mt-prog__status {
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.mt-prog__status.is-active {
  background: #e0f4ec;
  color: #0f9d6b;
}
.mt-prog__status.is-done {
  background: #eef1f7;
  color: #66708a;
}
.mt-prog__status.is-pending {
  background: #faf0dd;
  color: #c67c12;
}
.mt-prog__since {
  font-size: 11.5px;
  color: $faint;
}
.mt-ring {
  position: relative;
  width: 112px;
  height: 112px;
  display: grid;
  place-items: center;
  flex: none;
}
.mt-ring svg {
  transform: rotate(-90deg);
}
.mt-ring__fill {
  transition: stroke-dashoffset 0.8s ease;
}
.mt-ring__lbl {
  position: absolute;
  text-align: center;
}
.mt-ring__lbl b {
  display: block;
  font-size: 21px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.mt-ring__lbl span {
  font-size: 11px;
  opacity: 0.85;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* segmented */
.mt-seg {
  display: inline-flex;
  background: $surface;
  border: 1px solid $border;
  border-radius: 13px;
  padding: 4px;
  margin: 18px 0 4px;
  gap: 2px;
  box-shadow: 0 1px 2px rgba(27, 34, 51, 0.05);
}
.mt-seg button {
  font: inherit;
  font-size: 13.5px;
  font-weight: 700;
  border: none;
  background: none;
  color: $muted;
  padding: 8px 16px;
  border-radius: 10px;
  cursor: pointer;
  transition: 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.mt-seg__n {
  background: $surface-2;
  color: $muted;
  font-size: 11.5px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 999px;
  font-variant-numeric: tabular-nums;
}
.mt-seg button.is-active {
  background: $navy;
  color: #fff;
}
.mt-seg button.is-active .mt-seg__n {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

/* loading */
.mt-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  color: $muted;
  padding: 40px 4px;
  font-size: 14px;
}
.mt-spinner {
  width: 22px;
  height: 22px;
  border: 3px solid $border;
  border-top-color: $navy;
  border-radius: 50%;
  animation: mt-spin 0.8s linear infinite;
}
@keyframes mt-spin {
  to {
    transform: rotate(360deg);
  }
}

/* filters */
.mt-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 14px 0 8px;
}
.mt-chip {
  white-space: nowrap;
  border: 1px solid $border-strong;
  background: $surface;
  color: $muted;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 7px 13px;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  transition: 0.15s;
}
.mt-chip:hover {
  border-color: $navy;
  color: $ink;
}
.mt-chip.is-active {
  background: $navy;
  border-color: $navy;
  color: #fff;
}
.mt-chip__ico {
  width: 15px;
  height: 15px;
  display: inline-flex;
}
.mt-chip__ico ::v-deep svg {
  width: 15px;
  height: 15px;
}

/* groups */
.mt-group {
  margin-top: 20px;
}
.mt-group__head {
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0 2px 12px;
}
.mt-group__dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
}
.mt-group__head h2 {
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  margin: 0;
}
.mt-group__count {
  font-size: 12px;
  font-weight: 700;
  color: $muted;
  background: $surface;
  border: 1px solid $border;
  border-radius: 999px;
  padding: 1px 8px;
  font-variant-numeric: tabular-nums;
}
.mt-group__toggle {
  margin-left: auto;
  background: none;
  border: none;
  color: $muted;
  cursor: pointer;
}
.mt-group__toggle ::v-deep svg {
  width: 15px;
  height: 15px;
}
.g-overdue .mt-group__dot {
  background: #d63a45;
}
.g-overdue h2 {
  color: #d63a45;
}
.g-today .mt-group__dot {
  background: #c67c12;
}
.g-today h2 {
  color: #c67c12;
}
.g-upcoming .mt-group__dot {
  background: #3a63c4;
}
.g-upcoming h2 {
  color: #3a63c4;
}
.g-done .mt-group__dot {
  background: #0f9d6b;
}
.g-done h2 {
  color: #0f9d6b;
}

.mt-cards {
  display: flex;
  flex-direction: column;
  gap: 11px;
}
.mt-empty {
  color: $faint;
  font-size: 13px;
  font-style: italic;
  padding: 6px 2px;
}

.mt-card {
  display: flex;
  align-items: center;
  gap: 13px;
  background: $surface;
  border: 1px solid $border;
  border-radius: 16px;
  padding: 13px 15px;
  box-shadow: 0 1px 2px rgba(27, 34, 51, 0.05),
    0 10px 26px rgba(27, 34, 51, 0.06);
  transition: transform 0.28s ease, opacity 0.28s ease;
}
.mt-card.is-leaving {
  transform: translateX(16px) scale(0.98);
  opacity: 0;
}
.mt-card.is-done {
  opacity: 0.72;
}
.mt-card__ico {
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  background: var(--tint-soft, #{$surface-2});
  color: var(--tint, #{$ink});
}
.mt-card__ico ::v-deep svg {
  width: 18px;
  height: 18px;
  stroke-width: 1.8;
}
// Las clases del template son `type-` + t.type (plural): type-vitals, etc.
.type-vitals {
  --tint: #3a63c4;
  --tint-soft: #e7ecfb;
}
.type-medications {
  --tint: #{$teal};
  --tint-soft: #d9f2ee;
}
.type-checkins {
  --tint: #8b5cf6;
  --tint-soft: #efeafd;
}
.type-exercises {
  --tint: #e0713a;
  --tint-soft: #fbe9df;
}
.type-recommendations {
  --tint: #c67c12;
  --tint-soft: #faf0dd;
}
.type-appointments {
  --tint: #{$muted};
  --tint-soft: #{$surface-2};
}
.mt-card__body {
  flex: 1;
  min-width: 0;
}
.mt-card__name {
  font-weight: 600;
  font-size: 13.5px;
  letter-spacing: -0.01em;
  color: #0f172a;
}
.mt-card.is-done .mt-card__name {
  text-decoration: line-through;
  text-decoration-color: $faint;
}
.mt-card__meta {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 2px;
  color: $muted;
  font-size: 11.5px;
  flex-wrap: wrap;
}
.mt-card__kind {
  font-weight: 600;
  color: $faint;
}
.mt-card__when {
  font-variant-numeric: tabular-nums;
}
.mt-badge {
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.b-overdue {
  background: #fbe6e7;
  color: #d63a45;
}
.b-today {
  background: #faf0dd;
  color: #c67c12;
}
.b-upcoming {
  background: #e7ecfb;
  color: #3a63c4;
}
.b-done {
  background: #e1f4ec;
  color: #0f9d6b;
}

/* buttons */
// Botones alineados al estilo de "Mi seguimiento": chicos, acción en teal,
// secundarios outline. Sin navy pesado ni chevron (parecía dropdown).
.mt-btn {
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  border-radius: 8px;
  padding: 6px 13px;
  cursor: pointer;
  border: 1.5px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: 0.15s;
  white-space: nowrap;
}
.mt-btn ::v-deep svg {
  width: 14px;
  height: 14px;
}
.mt-btn[disabled] {
  opacity: 0.55;
  cursor: default;
}
.mt-btn--primary,
.mt-btn--teal {
  background: $teal;
  color: #fff;
  border-color: $teal;
}
.mt-btn--primary:hover,
.mt-btn--teal:hover {
  background: $teal-700;
  border-color: $teal-700;
}
.mt-btn--ghost {
  background: transparent;
  color: $muted;
  border-color: $border-strong;
}
.mt-btn--ghost:hover {
  border-color: $teal;
  color: $teal;
}
.mt-btn--done {
  background: transparent;
  color: #0f9d6b;
  border-color: transparent;
  cursor: default;
}
.mt-btn.is-full {
  flex: 1;
  justify-content: center;
  padding: 11px 13px;
}
// Todos los botones de acción de las cards del mismo ancho (Cargar / Responder
// / Tomé / Leer / Ver / Listo) para que la columna quede pareja.
.mt-card__action {
  flex: none;
}
.mt-card__action .mt-btn {
  min-width: 104px;
  justify-content: center;
}

/* recomendaciones */
.mt-rec-section {
  margin-top: 22px;
}
.mt-rec-section > h2 {
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0 2px 4px;
}
.mt-rec-desc {
  color: $muted;
  font-size: 13px;
  margin: 0 2px 14px;
}
.mt-rec-empty {
  color: $faint;
  font-size: 13px;
  font-style: italic;
  padding: 4px 2px 8px;
}
.mt-rec-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 14px;
}
.mt-rec-card {
  background: $surface;
  border: 1px solid $border;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(27, 34, 51, 0.05),
    0 10px 26px rgba(27, 34, 51, 0.06);
  cursor: pointer;
  transition: transform 0.18s, box-shadow 0.18s;
  display: flex;
  flex-direction: column;
}
.mt-rec-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 24px 50px rgba(20, 28, 54, 0.14);
}
.mt-rec-cover {
  height: 92px;
  display: grid;
  place-items: center;
  position: relative;
  color: #fff;
}
.mt-rec-cover svg {
  width: 32px;
  height: 32px;
  opacity: 0.95;
}
.cover-pat {
  background: #3d54b0;
}
.cover-camp {
  background: #0e8578;
}
.cover-inst {
  background: #64748b;
}
.cover-tip {
  background: #2e9e6c;
}
.mt-rec-tag {
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.mt-rec-body {
  padding: 13px 15px 15px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.mt-rec-body h3 {
  font-size: 14px;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.01em;
}
.mt-rec-body p {
  margin: 0;
  color: $muted;
  font-size: 12.5px;
  line-height: 1.5;
}
.mt-rec-read {
  margin-top: auto;
  color: $teal;
  font-weight: 700;
  font-size: 12.5px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding-top: 6px;
}
.mt-rec-read svg {
  width: 14px;
  height: 14px;
}

/* drawer (custom — Element el-drawer no está registrado en este build) */
.mt-scrim {
  position: fixed;
  inset: 0;
  background: rgba(16, 22, 40, 0.44);
  z-index: 3000;
}
.mt-drawer {
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: min(430px, 93vw);
  background: $surface;
  box-shadow: -12px 0 40px rgba(20, 28, 54, 0.16);
  transform: translateX(100%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 3001;
  overflow: hidden;
}
.mt-drawer.is-open {
  transform: none;
}
.mt-d {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.mt-d__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 20px 20px 16px;
  border-bottom: 1px solid $border;
}
.mt-d__head h3 {
  margin: 0;
  font-size: 16.5px;
  font-weight: 800;
  letter-spacing: -0.01em;
}
.mt-d__sub {
  color: $muted;
  font-size: 13px;
  margin-top: 2px;
}
.mt-d__close {
  margin-left: auto;
  background: $surface-2;
  border: 1px solid $border;
  border-radius: 9px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  color: $muted;
}
.mt-d__body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}
.mt-d__foot {
  padding: 16px 20px;
  border-top: 1px solid $border;
  display: flex;
  gap: 10px;
}
.mt-d__foot .mt-btn {
  flex: 1;
  justify-content: center;
  padding: 12px;
}
.mt-field {
  margin-bottom: 16px;
}
.mt-field label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: $muted;
  margin-bottom: 7px;
}
.mt-field input {
  width: 100%;
  font: inherit;
  font-size: 20px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  padding: 12px 14px;
  border: 1.5px solid $border-strong;
  border-radius: 12px;
  background: $surface-2;
  color: $ink;
}
.mt-field input:focus {
  outline: none;
  border-color: $navy;
  background: $surface;
}
.mt-range {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: $muted;
  margin-top: 10px;
}
.mt-range__pill {
  background: #e1f4ec;
  color: #0f9d6b;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 999px;
}
.mt-wiz-prog {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}
.mt-wiz-bar {
  flex: 1;
  height: 7px;
  background: $surface-2;
  border-radius: 999px;
  overflow: hidden;
  border: 1px solid $border;
}
.mt-wiz-bar i {
  display: block;
  height: 100%;
  background: $teal;
  border-radius: 999px;
  transition: width 0.3s;
}
.mt-wiz-prog span {
  font-size: 12px;
  font-weight: 700;
  color: $muted;
  font-variant-numeric: tabular-nums;
}
.mt-wiz-q {
  font-size: 16.5px;
  font-weight: 700;
  margin: 0 0 16px;
}
.mt-opt {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 13px 15px;
  border: 1.5px solid $border-strong;
  border-radius: 13px;
  margin-bottom: 10px;
  cursor: pointer;
  font-weight: 600;
  transition: 0.15s;
  background: $surface;
}
.mt-opt:hover {
  border-color: $navy;
}
.mt-opt.is-sel {
  border-color: $navy;
  background: $surface-2;
}
.mt-opt__rc {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid $border-strong;
  flex: none;
  display: grid;
  place-items: center;
}
.mt-opt.is-sel .mt-opt__rc {
  border-color: $navy;
  background: $navy;
}
.mt-opt.is-sel .mt-opt__rc::after {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fff;
}
.mt-media {
  border-radius: 14px;
  height: 150px;
  display: grid;
  place-items: center;
  color: #fff;
  margin-bottom: 16px;
}
.mt-media ::v-deep svg {
  width: 36px;
  height: 36px;
}
.mt-rtext {
  font-size: 13.5px;
  color: $ink;
  line-height: 1.6;
}
.mt-muted {
  color: $muted;
}
/* contenido de recomendación (media real / YouTube / texto) */
.mt-rec-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.mt-rec-content ::v-deep .mt-embed {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
}
.mt-rec-content ::v-deep .mt-embed iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
}
.mt-rec-content ::v-deep .mt-media-el {
  width: 100%;
  border-radius: 12px;
  display: block;
}
.mt-rec-content ::v-deep .mt-rtext {
  font-size: 13.5px;
  color: $ink;
  line-height: 1.6;
  margin: 0;
}
.mt-rec-content ::v-deep .mt-rec-link {
  color: $teal;
  font-weight: 600;
  font-size: 13px;
  text-decoration: none;
}
.mt-rec-content ::v-deep .mt-empty-content {
  color: $faint;
  font-size: 13px;
  text-align: center;
  padding: 40px 12px;
  font-style: italic;
}
.mt-exp {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.mt-exp__m {
  background: $surface-2;
  border: 1px solid $border;
  border-radius: 11px;
  padding: 9px 13px;
  text-align: center;
  flex: 1;
  min-width: 76px;
}
.mt-exp__m b {
  display: block;
  font-size: 17px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.mt-exp__m span {
  font-size: 11px;
  color: $muted;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

@media (max-width: 560px) {
  .mt-hero__hi {
    font-size: 20px;
  }
  .mt-card {
    flex-wrap: wrap;
  }
  .mt-card__action {
    width: 100%;
  }
  .mt-card__action .mt-btn {
    width: 100%;
    justify-content: center;
  }
  .mt-seg {
    display: flex;
    width: 100%;
  }
  .mt-seg button {
    flex: 1;
    justify-content: center;
  }
}
</style>
